import {
  Stack,
  Typography,
  IconButton,
  DialogContent,
  useTheme,
  Button,
  Checkbox,
  Dialog,
  Box,
  Link,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Controller, get, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { EditorToolBar } from './editor-tool-bar';
import {
  CustomAvatar,
  CustomAvatarGroup,
  FormProvider,
  Iconify,
  isOverDue,
  RHFTextField,
  RHFMentionInput,
} from '@app/components';
import { Strings } from '@app/constants';
import { useAuthRolePermissions } from '@app/utils';
import { useRequestState } from '@app/hooks';
import { Header } from '@app/sections/header';
import { selectMyProfile, selectMyProfileFormState } from '@app/modules/my-profile';
import { SideBar } from './side-bar';
import { AttachmentCard } from './attachment-card';
import {
  IAddDescriptionSchema,
  IChecklist,
  ICommentsFormData,
  IImageUrl,
  IMember,
  ITaskData,
} from '../types';
import { LabelStack, StyledDueDate, StyledImageStack } from '../styles';
import {
  selectAddDescriptionState,
  selectBoardDetailsState,
  selectCardDetailState,
  selectCommentsState,
  selectReplyCommentState,
  selectUpdateCommentState,
} from '../selector';
import {
  addComments,
  addDate,
  addDescription,
  deleteCommentByID,
  getCardDetailsByID,
  getComments,
  getCommentsSuccess,
  getTaskBoard,
  replyComment,
  updateCardTitle,
  updateCommentByID,
  addReaction,
} from '../slice';
import { AddTimeLimitPopup } from './add-time-limit-popup';
import { AddCheckListItem } from './add-checklist-item';
import {
  connectToCommentSocket,
  disconnectCommentSocket,
  emitCommentUpdate,
  HighlightMentions,
  parseMentions,
} from '../utils';
import { Reactions } from '@app/components/reaction/reaction';
import { Transactions } from './transactions';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  ids?: any;
  data: ITaskData | null;
  isLoggedInUser: boolean;
}

const CardDetails = ({ open, onClose, ids, isLoggedInUser }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { data: userData } = useRequestState({
    stateSelector: selectMyProfileFormState,
    errorShown: true,
  });

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState(false);
  const [dueStatus, setDueStatus] = useState('');
  const [openTimeLimitPopup, setOpenTimeLimitPopup] = useState<HTMLButtonElement | null>(null);
  const [editorValue, setEditorValue] = useState('');
  const [useRichEditor, setUseRichEditor] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const handleHeaderClick = () => {
    setIsEditing(true);
  };

  const { data: cardDetails } = useRequestState({
    stateSelector: selectCardDetailState,
    errorShown: true,
  });

  const { data: boardData } = useRequestState({
    stateSelector: selectBoardDetailsState,
    errorShown: true,
  });

  const methods = useForm<any>({
    defaultValues: {
      description: get(cardDetails, 'description', ''),
      isCompleted: get(cardDetails?.dueDates, 'isCompleted'),
      name: get(cardDetails, 'name', ''),
      updatedName: get(cardDetails, 'name', ''),
      comment: '',
    },
  });

  useEffect(() => {
    if (cardDetails?.dueDates) {
      methods.setValue('isCompleted', cardDetails?.dueDates?.isCompleted);
      const endDate: string = cardDetails.dueDates.endDate?.split('T')[0];
      const endTime: string = cardDetails.dueDates.endTime?.split('T')[1];
      setDueStatus(isOverDue(endDate, endTime));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardDetails]);

  const addDescriptionSuccess = useCallback(() => {
    setIsFocused(false);
    setUseRichEditor(false);
  }, []);

  const { loading } = useRequestState({
    stateSelector: selectAddDescriptionState,
    onSuccess: addDescriptionSuccess,
    errorShown: true,
  });

  const userDetails = useSelector(selectMyProfile);

  const coverImageFound = cardDetails?.attachments?.filter(
    (attachment: IImageUrl) => attachment?.isCoverImage
  );

  const onSubmit = (form: IAddDescriptionSchema) => {
    const descriptionToSubmit = useRichEditor ? editorValue : form.description;
    dispatch(addDescription({ ...form, description: descriptionToSubmit, ...ids }));
  };

  const onCommentSubmit = (data: any) => {
    const commentValue = data?.comment?.trim() ?? '';
    const { name: plainContent, mentionedUserIds } = parseMentions(commentValue);

    if (!plainContent) return;

    dispatch(
      addComments({
        comment: plainContent,
        mentionedUserIds,
        cardId: ids?.cardId,
      })
    );
    emitCommentUpdate({
      comment: plainContent,
      mentionedUserIds,
      cardId: ids?.cardId,
    });
    methods.setValue('comment', '');
    setShowDetails(true);
  };

  useEffect(() => {
    if (cardDetails?.description === null) {
      methods.setValue('description', '');
      setEditorValue('');
      setIsFocused(true);
    }
    if (cardDetails?.name === null) {
      methods.setValue('updatedName', '');
      setIsEditing(true);
    } else {
      methods.setValue('description', cardDetails?.description);
      setEditorValue(cardDetails?.description || '');
      methods.setValue('name', cardDetails?.name);
      methods.setValue('updatedName', cardDetails?.name);
      setIsFocused(false);
    }
  }, [cardDetails?.description, cardDetails?.name, methods]);

  const description = useWatch({ control: methods.control, name: 'description' });
  const updatedName = methods.watch('updatedName');
  const comment = useWatch({ control: methods.control, name: 'comment' });
  const { hasPermission } = useAuthRolePermissions();
  const canAddComments = hasPermission('Comments', 'w');

  useEffect(() => {
    if (ids && userData?.id) {
      dispatch(getCardDetailsByID({ ...ids, userId: userData.id }));
    }
  }, [dispatch, ids, userData?.id]);

  const handleTextFieldBlur = () => {
    if (updatedName !== cardDetails?.name) {
      dispatch(
        updateCardTitle({
          description,
          cardId: cardDetails?.id,
          name: updatedName,
        })
      );
      dispatch(getTaskBoard({ userId: userData?.id }));
    }
    setIsEditing(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setUseRichEditor(true);
    setEditorValue(description || '');
  };

  const handleSimpleEditorClick = () => {
    setIsFocused(true);
    setUseRichEditor(false);
  };

  const FormattedDate = `${
    cardDetails?.dueDates?.startDate
      ? dayjs(cardDetails?.dueDates?.startDate).format('MMM DD') + ', '
      : ''
  }${
    cardDetails?.dueDates?.startTime
      ? dayjs(cardDetails?.dueDates?.startTime).format('hh:mm A') + ' - '
      : ''
  }${
    cardDetails?.dueDates?.endDate
      ? dayjs(cardDetails?.dueDates?.endDate).format('MMM DD') + ', '
      : ''
  }${
    cardDetails?.dueDates?.endTime ? dayjs(cardDetails?.dueDates?.endTime).format('hh:mm A') : ''
  }`;

  const handleOpenDatePicker = (event: React.MouseEvent<HTMLDivElement>) => {
    setOpenTimeLimitPopup(event.currentTarget as any);
  };
  const handleCloseDatePicker = () => {
    setOpenTimeLimitPopup(null);
  };
  const { data: commentsData } = useRequestState({
    stateSelector: selectCommentsState,
    errorShown: true,
  });

  useEffect(() => {
    dispatch(getComments({ cardId: ids?.cardId }));
  }, [dispatch, ids?.cardId]);

  useEffect(() => {
    if (!ids?.cardId) return;

    const handleSocketData = (socketData: any) => {
      dispatch(
        getCommentsSuccess({
          comments: socketData.comments,
        })
      );
    };

    connectToCommentSocket(ids.cardId, handleSocketData);

    return () => {
      disconnectCommentSocket();
    };
  }, [ids?.cardId, dispatch, commentsData?.comments]);

  const handleDeleteComment = (commentId: string | number) => {
    dispatch(deleteCommentByID({ commentId }));
  };

  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);

  const handleEditComment = (comment: any) => {
    setEditingCommentId(comment.id);
    methods.setValue('name', comment?.name || '');
  };

  const handleSave = (comment: any): void => {
    const editedName = methods.getValues('name')?.trim() || '';
    const { name: plainContent, mentionedUserIds } = parseMentions(editedName);

    if (!plainContent) return;

    dispatch(
      updateCommentByID({
        commentId: comment.id,
        mentionedUserIds,
        name: plainContent,
      })
    );
    emitCommentUpdate({
      commentId: comment.id,
      mentionedUserIds,
      name: plainContent,
    });
    setEditingCommentId(null);
  };

  const handleCancel = () => {
    setEditingCommentId(null);
  };

  const handleCommentCancel = () => {
    methods.setValue('comment', '');
  };

  const { control, watch, setValue, getValues } = useForm<ICommentsFormData>({
    defaultValues: {
      comments: [],
      selectedEmoji: '',
    },
  });

  const { update: updateComment } = useFieldArray({
    control,
    name: 'comments',
  });

  useEffect(() => {
    if (commentsData?.comments) {
      const currentComments = getValues('comments');
      const initialComments = (commentsData?.comments ?? []).map((comment: any, index: number) => {
        const existingComment = currentComments[index];
        return {
          id: comment.id ?? (commentsData?.comments ?? [])[index]?.id,
          replies: existingComment?.replies ?? [],
          replyInput: existingComment?.replyInput ?? '',
          isReplying: existingComment?.isReplying ?? false,
          editingReplyIndex: existingComment?.editingReplyIndex ?? null,
          editingReplyValue: existingComment?.editingReplyValue ?? '',
        };
      });

      if (JSON?.stringify(currentComments) !== JSON?.stringify(initialComments)) {
        setValue('comments', initialComments);
      }
    }
  }, [commentsData?.comments?.length, commentsData?.comments, getValues, setValue]);

  const handleEmojiSelect = (emoji: string, commentId: number) => {
    dispatch(
      addReaction({
        reaction: emoji,
        commentId: String(commentId),
      })
    );
    setValue('selectedEmoji', emoji);
  };

  const handleReplySuccess = useCallback(() => {
    dispatch(getComments({ cardId: ids?.cardId }));
  }, [dispatch, ids?.cardId]);

  const handleReplySubmit = ({
    index,
    commentId,
  }: {
    index: number;
    commentId: number | string;
  }) => {
    const currentComments = getValues('comments');
    const comment = currentComments[index];
    const name = comment?.replyInput?.trim() ?? '';

    if (!name) return;
    dispatch(replyComment({ commentId, name }));
    handleReplyCancel(index);
  };

  useRequestState({
    stateSelector: selectReplyCommentState,
    successMessageShown: true,
    errorShown: true,
    onSuccess: handleReplySuccess,
  });

  const handleUpdateCommentSuccess = useCallback(() => {
    dispatch(getComments({ cardId: ids?.cardId }));
  }, [dispatch, ids?.cardId]);

  useRequestState({
    stateSelector: selectUpdateCommentState,
    successMessageShown: true,
    errorShown: true,
    onSuccess: handleUpdateCommentSuccess,
  });

  const handleReplyCancel = (commentIndex: number) => {
    const currentComments = getValues('comments');
    const comment = currentComments[commentIndex] ?? {};
    const updatedComment = {
      ...comment,
      id: comment.id ?? (commentsData?.comments ?? [])[commentIndex]?.id,
      replies: comment.replies ?? [],
      replyInput: '',
      isReplying: false,
      editingReplyIndex: comment.editingReplyIndex ?? null,
      editingReplyValue: comment.editingReplyValue ?? '',
    };

    updateComment(commentIndex, updatedComment);
  };

  const handleReplyInputChange = (commentIndex: number, value: string) => {
    const currentComments = getValues('comments');
    const comment = currentComments[commentIndex] ?? {};
    const updatedComment = {
      ...comment,
      id: comment.id ?? (commentsData?.comments ?? [])[commentIndex]?.id,
      replies: comment.replies ?? [],
      replyInput: value,
      isReplying: comment?.isReplying ?? true,
      editingReplyIndex: comment.editingReplyIndex ?? null,
      editingReplyValue: comment.editingReplyValue ?? '',
    };
    updateComment(commentIndex, updatedComment);
  };

  const handleStartReplying = (commentIndex: number) => {
    const currentComments = getValues('comments');
    const comment = currentComments[commentIndex] ?? {};
    const updatedComment = {
      ...comment,
      id: (commentsData?.comments ?? [])[commentIndex]?.id,
      replies: comment.replies ?? [],
      isReplying: true,
      replyInput: '',
      editingReplyIndex: comment.editingReplyIndex ?? null,
      editingReplyValue: comment.editingReplyValue ?? '',
    };
    updateComment(commentIndex, updatedComment);
  };

  const handleEditReply = (commentIndex: number, replyIndex: number, reply: any) => {
    const currentComments = getValues('comments');
    const comment = currentComments[commentIndex];
    const updatedComment = {
      ...comment,
      editingReplyIndex: replyIndex,
      editingReplyValue: reply.name ?? reply.text,
    };
    updateComment(commentIndex, updatedComment);
  };

  const handleSaveReplyEdit = (commentIndex: number, reply: any) => {
    const currentComments = getValues('comments');
    const comment = currentComments[commentIndex];

    if (!comment?.editingReplyValue?.trim() || comment.editingReplyIndex === null) return;

    dispatch(
      updateCommentByID({
        commentId: reply?.id,
        name: comment?.editingReplyValue?.trim(),
        mentionedUserIds: [],
      })
    );

    const updatedComments = [...currentComments];
    updatedComments[commentIndex] = {
      ...comment,
      editingReplyIndex: null,
      editingReplyValue: '',
    };

    setValue('comments', updatedComments);
  };

  const handleCancelReplyEdit = (commentIndex: number) => {
    const currentComments = getValues('comments');
    const comment = currentComments[commentIndex];
    const updatedComment = {
      ...comment,
      editingReplyIndex: null,
      editingReplyValue: '',
    };
    updateComment(commentIndex, updatedComment);
  };

  const handleReplyEditValueChange = (commentIndex: number, value: string) => {
    const currentComments = getValues('comments');
    const comment = currentComments[commentIndex];
    const updatedComment = {
      ...comment,
      editingReplyValue: value,
    };
    updateComment(commentIndex, updatedComment);
  };

  const handleDeleteReply = (reply: any) => {
    dispatch(deleteCommentByID({ commentId: reply.id }));
  };

  const watchedComments = watch('comments');

  return (
    <>
      <FormProvider methods={methods}>
        <Dialog
          open={open}
          sx={{
            '& .MuiDialog-paper': {
              width: '60%',
              maxWidth: 'none',
              margin: 0,
              backgroundColor: theme.palette.grey[200],
            },
          }}
        >
          <DialogContent sx={{ width: '100%', padding: 0 }}>
            <Stack
              sx={{
                width: '100%',
                alignItems: 'stretch',
              }}
            >
              {coverImageFound && coverImageFound.length > 0 && (
                <Stack
                  sx={{
                    width: '100%',
                    backgroundColor: theme.palette.grey[800],
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <StyledImageStack
                    url={coverImageFound[0]?.attachment}
                    height={'170px'}
                    width={'50%'}
                  />
                </Stack>
              )}
              <Stack sx={{ padding: 2 }}>
                <Stack marginTop={0} spacing={isEditing ? 2 : -2} mt={isEditing ? 0 : -2}>
                  {isEditing ? (
                    <RHFTextField
                      size="small"
                      name="updatedName"
                      onBlur={handleTextFieldBlur}
                      autoFocus
                      sx={{
                        width: '45%',
                        height: '20%',
                      }}
                    />
                  ) : (
                    <Stack onClick={handleHeaderClick} sx={{ width: '100%', cursor: 'pointer' }}>
                      <Header variant="dialog" title={cardDetails?.name} onClose={onClose} />
                    </Stack>
                  )}
                  {
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.grey[600], pl: 2 }}
                      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    >{`in list ${cardDetails?.listStatus}`}</Typography>
                  }
                </Stack>
                <Stack
                  sx={{
                    flexDirection: 'row',
                    width: '97%',
                    justifyContent: 'space-between',
                  }}
                  ml={2}
                >
                  <Stack mt={4} sx={{ width: '67%' }}>
                    <Stack sx={{ flexDirection: 'row' }} flexWrap={'wrap'}>
                      <Stack sx={{ mr: cardDetails?.members?.length ? 1.5 : 0 }}>
                        {cardDetails?.members?.length > 0 && (
                          <Stack>
                            <Typography variant="body2" color={theme.palette.grey[700]}>
                              {Strings.taskBoard.members}
                            </Typography>
                            <Stack sx={{ flexDirection: 'row' }}>
                              <CustomAvatarGroup max={10}>
                                {cardDetails?.members?.length > 0 &&
                                  cardDetails?.members?.map((member: IMember, index: number) => (
                                    <CustomAvatar
                                      key={index}
                                      name={`${member?.firstName} ${member?.lastName} `}
                                      src={member?.profileImage}
                                      style={{
                                        backgroundColor: theme.palette.taskboard.raisinBlack,
                                        color: theme.palette.common.white,
                                        width: '35px',
                                        height: '35px',
                                        fontSize: '12px',
                                      }}
                                    />
                                  ))}
                              </CustomAvatarGroup>
                            </Stack>
                          </Stack>
                        )}
                      </Stack>
                      {cardDetails?.addedLabels && cardDetails?.addedLabels?.length > 0 && (
                        <Stack>
                          <Typography variant="body2" color={theme.palette.grey[700]}>
                            {Strings.taskBoard.labels}
                          </Typography>
                          <Stack direction={'row'} flexWrap={'wrap'} gap={1}>
                            {cardDetails?.addedLabels?.map((label: any, index: number) => (
                              <LabelStack
                                key={index}
                                labelDataColor={label?.color}
                                name={label?.name}
                              >
                                {label?.name}
                              </LabelStack>
                            ))}
                          </Stack>
                        </Stack>
                      )}
                      <Stack sx={{ justifyContent: 'center' }}>
                        {cardDetails?.dueDates && (
                          <Stack>
                            <Typography variant="body2" color={theme.palette.grey[700]}>
                              {Strings.taskBoard.dueDate}
                            </Typography>
                            <Stack
                              sx={{
                                alignItems: 'center',
                                flexDirection: 'row',
                                justifyContent: 'space-around',
                              }}
                            >
                              <Controller
                                name="isCompleted"
                                render={({ field }) => (
                                  <Checkbox
                                    {...field}
                                    checked={cardDetails?.dueDates?.isCompleted}
                                    sx={{
                                      '&.Mui-checked': {
                                        color: theme.palette.taskboard.raisinBlack,
                                      },
                                      ml: -1.5,
                                    }}
                                    onChange={(e) => {
                                      field.onChange(e.target.checked);
                                      dispatch(
                                        addDate({
                                          startDate: cardDetails?.dueDates?.startDate,
                                          endDate: cardDetails?.dueDates?.endDate,
                                          endTime: cardDetails?.dueDates?.endTime,
                                          startTime: cardDetails?.dueDates?.startTime,
                                          isCompleted: e.target.checked,
                                          id: cardDetails?.dueDates?.id,
                                          status: e.target.checked
                                            ? Strings.status.completed
                                            : Strings.status.inProgress,
                                          cardId: cardDetails?.id,
                                        })
                                      );
                                      onClose();
                                    }}
                                  />
                                )}
                              />
                              <Stack
                                sx={{
                                  backgroundColor: theme.palette.grey[300],
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  borderRadius: '4px',
                                }}
                                p={0.5}
                                gap={1}
                              >
                                <Stack
                                  sx={{
                                    fontSize: '14px',
                                    justifyContent: 'center',
                                  }}
                                >
                                  {FormattedDate}
                                </Stack>
                                <Stack>
                                  {isLoggedInUser && (
                                    <Iconify icon="formkit:down" onClick={handleOpenDatePicker} />
                                  )}
                                </Stack>
                                <AddTimeLimitPopup
                                  openTimeLimitPopup={openTimeLimitPopup}
                                  handleCloseDatePicker={handleCloseDatePicker}
                                  cardId={ids?.cardId}
                                  onClose={onClose}
                                  startsDate={cardDetails?.dueDates?.startDate}
                                  endDate={cardDetails?.dueDates?.endDate}
                                  endTime={cardDetails?.dueDates?.endTime}
                                  startTime={cardDetails?.dueDates?.startTime}
                                  update
                                />
                                <StyledDueDate
                                  checked={cardDetails?.dueDates?.isCompleted}
                                  status={dueStatus}
                                >
                                  {cardDetails?.dueDates?.isCompleted
                                    ? Strings.taskBoard.completed
                                    : dueStatus === 'dueSoon'
                                    ? Strings.taskBoard.dueSoon
                                    : dueStatus === 'overDue'
                                    ? Strings.taskBoard.overDue
                                    : ''}
                                </StyledDueDate>
                              </Stack>
                            </Stack>
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                    <Stack sx={{ flexDirection: 'row' }} mt={3}>
                      <Stack sx={{ ml: -1 }}>
                        <IconButton
                          aria-label="close"
                          sx={{
                            color: theme.palette.taskboard.deepPurple,
                          }}
                        >
                          <Iconify icon={'fluent:text-description-32-filled'} />
                        </IconButton>
                      </Stack>
                      <Stack sx={{ width: '90%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography
                            sx={{ fontWeight: 500, color: theme.palette.grey['800'] }}
                            ml={1}
                            mt={1}
                          >
                            {Strings.taskBoard.description}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                    <Stack>
                      {!isFocused && (
                        <Stack
                          onClick={handleFocus}
                          mt={1}
                          mb={2}
                          width="100%"
                          minHeight={'60px'}
                          sx={{
                            border: `0.5px solid ${
                              isFocused ? theme.palette.grey[800] : theme.palette.grey[300]
                            }`,
                            flexWrap: 'wrap',
                            borderRadius: 2,
                            wordWrap: 'break-word',
                            overflow: 'hidden',
                            maxWidth: '100%',
                            pl: 1,
                            pt: 0.5,
                            pr: 1,
                            pb: 1,
                            cursor: 'pointer',
                          }}
                        >
                          <Stack>
                            <Typography
                              component="div"
                              dangerouslySetInnerHTML={{ __html: cardDetails?.description || '' }}
                              sx={{
                                wordBreak: 'break-word',
                              }}
                            />
                          </Stack>
                        </Stack>
                      )}

                      {isFocused && (
                        <>
                          <Stack gap={1} mt={1}>
                            {!useRichEditor && (
                              <RHFTextField
                                multiline
                                name="description"
                                onFocus={handleSimpleEditorClick}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                      borderColor: theme.palette.grey[300],
                                    },
                                    minHeight: '60px',
                                    height: 'auto',
                                    pl: 1,
                                    pt: 0.5,
                                    pr: 1,
                                    pb: 1,
                                  },
                                  '& .MuiInputBase-input': {
                                    minHeight: '40px',
                                    height: 'auto',
                                    p: 0,
                                    overflow: 'auto',
                                  },
                                }}
                              />
                            )}

                            {useRichEditor && (
                              <EditorToolBar value={editorValue} onChange={setEditorValue} />
                            )}
                          </Stack>

                          <Stack
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              mt: 10,
                            }}
                          >
                            <LoadingButton
                              loading={loading}
                              variant="contained"
                              color="primary"
                              sx={{
                                mr: 1,
                                bgcolor: theme.palette.taskboard.raisinBlack,
                                '&:hover': {
                                  bgcolor: theme.palette.taskboard.raisinBlack,
                                },
                              }}
                              onClick={methods.handleSubmit(onSubmit)}
                            >
                              {Strings.button.save}
                            </LoadingButton>
                            <Button
                              onClick={() => {
                                setIsFocused(false);
                                setUseRichEditor(false);
                              }}
                              sx={{
                                '&:hover': {
                                  bgcolor: theme.palette.grey[500],
                                  color: theme.palette.error.contrastText,
                                },
                                color: theme.palette.taskboard.raisinBlack,
                                border: theme.palette.taskboard.raisinBlack,
                              }}
                            >
                              {Strings.button.cancel}
                            </Button>
                          </Stack>
                        </>
                      )}
                    </Stack>
                    {cardDetails?.checklists &&
                      cardDetails?.checklists?.length > 0 &&
                      cardDetails?.checklists?.map((check: IChecklist, index: number) => (
                        <AddCheckListItem check={check} key={index} />
                      ))}
                    {cardDetails?.attachments && cardDetails?.attachments?.length > 0 && (
                      <Stack
                        sx={{
                          flexDirection: 'row',
                        }}
                        mt={2}
                      >
                        <Stack>
                          <IconButton
                            aria-label="close"
                            sx={{
                              color: theme.palette.taskboard.raisinBlack,
                            }}
                          >
                            <Iconify icon={'typcn:attachment'} mt={0.4} />
                          </IconButton>
                        </Stack>

                        <Stack sx={{ width: '100%' }}>
                          <AttachmentCard imageUrl={cardDetails?.attachments} />
                        </Stack>
                      </Stack>
                    )}
                    <Stack
                      mt={4}
                      sx={{
                        width: '100%',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Stack
                        sx={{
                          alignItems: 'center',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          ml: -1,
                        }}
                      >
                        <IconButton
                          aria-label="close"
                          sx={{
                            color: theme.palette.taskboard.deepPurple,
                          }}
                        >
                          <Iconify icon={'fluent:text-description-32-filled'} />
                        </IconButton>
                        <Typography
                          ml={1}
                          sx={{ fontWeight: 500, color: theme.palette.grey['800'] }}
                        >
                          {Strings.taskBoard.activity}
                        </Typography>
                      </Stack>

                      <Stack>
                        <LabelStack
                          labelDataColor={theme.palette.grey['300']}
                          name={
                            showDetails
                              ? Strings.taskBoard.showDetails
                              : Strings.taskBoard.hideDetails
                          }
                          onClick={() => {
                            setShowDetails((prev) => !prev);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          {showDetails
                            ? Strings.taskBoard.hideDetails
                            : Strings.taskBoard.showDetails}
                        </LabelStack>
                      </Stack>
                    </Stack>
                    <Stack my={2}>
                      <Stack sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                        <CustomAvatar
                          name={`${userDetails?.firstName ?? ''} ${userDetails?.lastName ?? ''}`}
                          src={userDetails?.profileImage || undefined}
                          color={'default'}
                          sx={{
                            backgroundColor: theme.palette.taskboard.raisinBlack,
                            color: theme.palette.common.white,
                            width: '35px',
                            height: '35px',
                            fontSize: '12px',
                            '& img': {
                              objectFit: 'cover',
                              width: '100%',
                              height: '100%',
                            },
                          }}
                        />
                        <Stack
                          sx={{
                            width: '100%',
                            backgroundColor: theme.palette.common.white,
                            borderRadius: 2,
                          }}
                        >
                          <RHFMentionInput
                            name="comment"
                            disabled={!canAddComments}
                            placeHolder={Strings.taskBoard.writeAComment}
                            mentionData={boardData?.members?.map((member: any) => ({
                              id: member?.membersUserId,
                              display: member?.firstName?.trim(),
                            }))}
                          />
                        </Stack>
                      </Stack>
                      {comment && (
                        <Stack
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            ml: 7,
                            my: 1,
                          }}
                        >
                          <LoadingButton
                            loading={loading}
                            variant="contained"
                            color="primary"
                            sx={{
                              mr: 1,
                              bgcolor: theme.palette.taskboard.raisinBlack,
                              '&:hover': {
                                bgcolor: theme.palette.taskboard.raisinBlack,
                              },
                            }}
                            onClick={methods.handleSubmit(onCommentSubmit)}
                          >
                            {Strings.button.comment}
                          </LoadingButton>
                          <Button
                            onClick={handleCommentCancel}
                            sx={{
                              '&:hover': {
                                bgcolor: theme.palette.taskboard.raisinBlack,
                                color: theme.palette.error.contrastText,
                              },
                              color: theme.palette.taskboard.raisinBlack,
                              border: theme.palette.taskboard.raisinBlack,
                            }}
                          >
                            {Strings.button.cancel}
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                  <SideBar ids={ids} onClose={onClose} isLoggedInUser={isLoggedInUser} />
                </Stack>

                {showDetails && (
                  <>
                    <Stack sx={{ mt: 2 }} />
                    {(commentsData?.comments ?? [])
                      .filter((comment: any) => {
                        const isReply = (commentsData?.comments ?? []).some((otherComment: any) =>
                          (otherComment.replies ?? []).some((reply: any) => reply.id === comment.id)
                        );
                        return !isReply;
                      })
                      ?.map((comment: any, index: number) => {
                        const isCommentWriter = comment?.createdBy === userDetails?.id;

                        const formComment = watchedComments[index] ?? {
                          replies: [],
                          replyInput: '',
                          isReplying: false,
                          editingReplyIndex: null,
                          editingReplyValue: '',
                        };

                        return (
                          <Stack
                            key={comment?.id}
                            direction="row"
                            spacing={2}
                            sx={{
                              backgroundColor: theme.palette.grey[200],
                              borderRadius: 2,
                              my: 0.5,
                              alignItems: 'flex-start',
                              ml: 2,
                            }}
                          >
                            <CustomAvatar
                              name={`${String(comment?.firstName ?? '')} ${String(
                                comment?.lastName ?? ''
                              )}`}
                              src={comment?.profileImage ?? undefined}
                              color={'default'}
                              sx={{
                                backgroundColor: theme.palette.taskboard.raisinBlack,
                                color: theme.palette.common.white,
                                width: '35px',
                                height: '35px',
                                fontSize: '12px',
                              }}
                            />
                            <Box flex={1}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography fontWeight="bold">
                                  {comment?.firstName ?? comment?.lastName
                                    ? `${String(comment?.firstName ?? '')} ${String(
                                        comment?.lastName ?? ''
                                      )}`.trim()
                                    : 'Unknown'}
                                </Typography>
                                <Link
                                  underline="hover"
                                  sx={{ fontSize: '0.85rem', color: theme.palette.grey[700] }}
                                >
                                  {new Date(comment?.createdAt).toLocaleString(undefined, {
                                    year: 'numeric',
                                    month: 'numeric',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: 'numeric',
                                  })}
                                </Link>
                              </Stack>
                              <Box
                                sx={{
                                  backgroundColor: theme.palette.common.white,
                                  borderRadius: 1,
                                  p: 1.5,
                                  border: `0.5px solid ${theme.palette.grey[300]}`,
                                  maxWidth: '600px',
                                }}
                              >
                                {editingCommentId === comment.id ? (
                                  <RHFMentionInput
                                    name="name"
                                    disabled={!canAddComments}
                                    mentionData={boardData?.members?.map((member: any) => ({
                                      id: member?.membersUserId,
                                      display: member?.firstName?.trim(),
                                    }))}
                                  />
                                ) : (
                                  <Typography component="div" sx={{ whiteSpace: 'pre-line' }}>
                                    <HighlightMentions text={comment?.name} />
                                  </Typography>
                                )}
                              </Box>
                              <Stack direction="row" sx={{ flexWrap: 'wrap' }} spacing={1}>
                                <Reactions
                                  onSelect={(emoji) => {
                                    handleEmojiSelect(emoji, comment?.id);
                                  }}
                                  reactionData={comment?.reactions || []}
                                />
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => {
                                      handleStartReplying(index);
                                    }}
                                  >
                                    • {Strings.taskBoard.reply}
                                  </Typography>

                                  {isCommentWriter &&
                                    (editingCommentId === comment.id ? (
                                      <>
                                        <Typography
                                          variant="body2"
                                          color="primary"
                                          sx={{ cursor: 'pointer' }}
                                          onClick={() => {
                                            handleSave(comment);
                                          }}
                                        >
                                          • {Strings.taskBoard.save}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="error"
                                          sx={{ cursor: 'pointer' }}
                                          onClick={() => {
                                            handleCancel();
                                          }}
                                        >
                                          • {Strings.taskBoard.cancel}
                                        </Typography>
                                      </>
                                    ) : (
                                      <>
                                        <Typography
                                          variant="body2"
                                          color="textSecondary"
                                          sx={{ cursor: 'pointer' }}
                                          onClick={() => {
                                            handleEditComment(comment);
                                          }}
                                        >
                                          • {Strings.taskBoard.edit}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="textSecondary"
                                          sx={{ cursor: 'pointer' }}
                                          onClick={() => {
                                            handleDeleteComment(comment.id);
                                          }}
                                        >
                                          • {Strings.taskBoard.delete}
                                        </Typography>
                                      </>
                                    ))}
                                </Stack>
                              </Stack>
                              {formComment?.isReplying && (
                                <Box sx={{ mt: 1, ml: 5, maxWidth: '500px' }}>
                                  <RHFTextField
                                    name="reply"
                                    size="small"
                                    fullWidth
                                    placeholder="Write a reply..."
                                    value={formComment.replyInput}
                                    onChange={(e) => {
                                      handleReplyInputChange(index, e.target.value);
                                    }}
                                  />
                                  <Stack direction="row" spacing={1} mt={1}>
                                    <Typography
                                      variant="body2"
                                      color="primary"
                                      sx={{ cursor: 'pointer' }}
                                      onClick={() => {
                                        handleReplySubmit({ index, commentId: comment.id });
                                      }}
                                    >
                                      • {Strings.taskBoard.reply}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="error"
                                      sx={{ cursor: 'pointer' }}
                                      onClick={() => {
                                        handleReplyCancel(index);
                                      }}
                                    >
                                      • {Strings.taskBoard.cancel}
                                    </Typography>
                                  </Stack>
                                </Box>
                              )}
                              {comment.replies?.map((reply: any, replyIdx: number) => {
                                const isEditingReply = formComment.editingReplyIndex === replyIdx;
                                const isReplyWriter = reply?.createdBy === userDetails?.id;

                                return (
                                  <Stack
                                    key={reply?.id ?? replyIdx}
                                    direction="row"
                                    spacing={2}
                                    sx={{ mt: 1, ml: 3 }}
                                  >
                                    <CustomAvatar
                                      name={`${String(reply?.firstName ?? '')} ${String(
                                        reply?.lastName ?? ''
                                      )}`}
                                      src={reply?.profileImage ?? undefined}
                                      color={'default'}
                                      sx={{
                                        backgroundColor: theme.palette.taskboard.raisinBlack,
                                        color: theme.palette.common.white,
                                        width: '30px',
                                        height: '30px',
                                        fontSize: '10px',
                                        '& img': {
                                          objectFit: 'cover',
                                          width: '100%',
                                          height: '100%',
                                        },
                                      }}
                                    />
                                    <Box flex={1}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                                          {reply?.firstName ?? reply?.lastName
                                            ? `${String(reply?.firstName ?? '')} ${String(
                                                reply?.lastName ?? ''
                                              )}`.trim()
                                            : 'Unknown'}
                                        </Typography>
                                        <Link
                                          underline="hover"
                                          sx={{
                                            fontSize: '0.75rem',
                                            color: theme.palette.grey[700],
                                          }}
                                        >
                                          {new Date(
                                            reply?.createdAt ?? reply?.timestamp
                                          )?.toLocaleString(undefined, {
                                            year: 'numeric',
                                            month: 'numeric',
                                            day: 'numeric',
                                            hour: 'numeric',
                                            minute: 'numeric',
                                          })}
                                        </Link>
                                      </Stack>

                                      <Box
                                        sx={{
                                          backgroundColor: theme.palette.common.white,
                                          borderRadius: 1,
                                          p: 1.5,
                                          border: `0.5px solid ${theme.palette.grey[300]}`,
                                          maxWidth: '450px',
                                        }}
                                      >
                                        {isEditingReply ? (
                                          <RHFTextField
                                            name="editReply"
                                            size="small"
                                            fullWidth
                                            value={formComment.editingReplyValue}
                                            onChange={(e) => {
                                              handleReplyEditValueChange(index, e.target.value);
                                            }}
                                          />
                                        ) : (
                                          <Typography
                                            sx={{ whiteSpace: 'pre-line', fontSize: '0.9rem' }}
                                          >
                                            {reply?.name ?? reply?.text}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Stack
                                        direction="row"
                                        sx={{ flexWrap: 'wrap' }}
                                        spacing={1}
                                        mt={1}
                                      >
                                        <Box sx={{ position: 'relative', bottom: 6 }}>
                                          <Reactions
                                            onSelect={(emoji) => {
                                              handleEmojiSelect(emoji, reply.id);
                                            }}
                                            reactionData={reply?.reactions || []}
                                          />
                                        </Box>

                                        {isReplyWriter &&
                                          (isEditingReply ? (
                                            <>
                                              <Typography
                                                variant="body2"
                                                color="primary"
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                  handleSaveReplyEdit(index, reply);
                                                }}
                                              >
                                                • {Strings.taskBoard.save}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                color="error"
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                  handleCancelReplyEdit(index);
                                                }}
                                              >
                                                • {Strings.taskBoard.cancel}
                                              </Typography>
                                            </>
                                          ) : (
                                            <>
                                              <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                  handleEditReply(index, replyIdx, reply);
                                                }}
                                              >
                                                • {Strings.taskBoard.edit}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                  handleDeleteReply(reply);
                                                }}
                                              >
                                                • {Strings.taskBoard.delete}
                                              </Typography>
                                            </>
                                          ))}
                                      </Stack>
                                    </Box>
                                  </Stack>
                                );
                              })}
                            </Box>
                          </Stack>
                        );
                      })}
                    <Transactions ids={ids} />
                  </>
                )}
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>
      </FormProvider>
    </>
  );
};

export { CardDetails };
